// E-Ticaret › B2B Ayarları (B2B Yönetimi) kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'ecommerce.b2b';

export const ecommerceB2bGuide = (t: TFunction): GuideContent => ({
  layout: 'tabbed',
  title: t(`${G}.title`, 'B2B ayarları nasıl yapılır?'),
  intro: t(`${G}.intro`, 'Web sitenizin bireysel müşterilere (B2C), bayilere (B2B) ya da ikisine birden satış yapmasını, fiyatların hangi para biriminde gösterileceğini ve online tahsilatların hangi banka hesabına işleneceğini buradan belirlersiniz. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  navItems: [
    t(`${G}.nav.settings`, 'B2B Ayarları'),
    t(`${G}.nav.statement`, 'Cari Ekstre'),
  ],
  regionLabels: {
    tabs: t(`${G}.regions.tabs`, 'Sekmeler'),
    body: t(`${G}.regions.body`, 'Ayar kartları / ekstre'),
    footer: t(`${G}.regions.footer`, 'Kaydet'),
  },
  steps: [
    {
      region: 'body',
      title: t(`${G}.s1.title`, '1. Mağaza modunu seçin'),
      text: t(`${G}.s1.text`, '"Mağaza Modu" listesinden sitenin kime satış yapacağını seçin. Bu seçim listeden seçer seçmez kaydedilir.'),
      tips: [
        t(`${G}.s1.tip1`, 'Sadece B2C — B2B kapalı (varsayılan): yalnız bireysel müşteriler.'),
        t(`${G}.s1.tip2`, 'Hibrit — B2C + B2B birlikte: hem bireysel müşteriler hem bayiler.'),
        t(`${G}.s1.tip3`, 'Sadece B2B — yalnız onaylı bayiler: onaylı B2B üyesi olmayan mevcut B2C üyeler alışveriş yapamaz; B2C kayıt ayarı kapalıysa yeni son kullanıcı kaydı alınmaz.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s2.title`, '2. Giriş ve fiyat görünürlüğünü ayarlayın'),
      text: t(`${G}.s2.text`, 'Mağaza modunun yanındaki anahtarlarla kimin giriş yapacağını ve fiyatları kimin göreceğini belirleyin. "Giriş zorunlu", "Fiyatları sadece onaylı B2B (bayi) üyeler görebilsin", "B2C kullanıcı girişi", "Aquasoft ile giriş" ve "Şube girişine izin ver" anahtarları değiştirildiği anda kaydedilir.'),
      tips: [
        t(`${G}.s2.tip1`, 'Giriş zorunlu: siteye giren ziyaretçi giriş sayfasına yönlendirilir.'),
        t(`${G}.s2.tip2`, 'Aquasoft ile giriş: bayi kendi Aquasoft e-posta / şifresiyle girebilir. Şube girişi açıksa Aquasoft şube kullanıcıları da girer; her şube kendi carisine kaydolur.'),
        t(`${G}.s2.tip3`, '"Son kullanıcı (B2C) kaydına izin ver" ve "B2B/bayi kullanıcıya fiyatları KDV hariç göster" ise alttaki Kaydet düğmesiyle kaydedilir.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s3.title`, '3. Para birimi ve kur modunu seçin'),
      text: t(`${G}.s3.text`, '"Döviz Ayarları" kartında "Mağaza Para Birimi" sitede fiyatların gösterildiği para birimidir. "Kur Modu" Manuel ise "Manuel Kur" alanına 1 birim dövizin kaç TL olduğunu yazın; TL dışı para biriminde manuel kur boş bırakılırsa kayıt yapılmaz. Otomatik seçilirse günlük TCMB kuru kullanılır.'),
      tips: [
        t(`${G}.s3.tip1`, '"Bireysel (B2C) müşteriye fiyatları TL göster" açıkken bireysel müşteriler ürün, sepet ve ödemede tutarları kur ile TL\'ye çevrilmiş görür ve TL öder; bayiler mağaza para biriminde görmeye devam eder. Kapalıyken herkes mağaza para biriminde görür, online ödemede tutar kurla TL\'ye çevrilerek tahsil edilir.'),
        t(`${G}.s3.tip2`, '"Kuru vitrin header\'ında göster" açıksa sitenin üst alanında USD/EUR kuru görünür. Otomatik modda kurlar TCMB\'den alınır: kaydettiğinizde hemen çekilir, sonra günlük yenilenir.'),
        t(`${G}.s3.tip3`, 'Manuel modda üst alandaki kurlar Döviz tanımlarındaki (Currencies) USD/EUR kayıtlarından okunur; kuru girilmemiş (1,00) kayıtlar gösterilmez. Kurların kendiliğinden güncellenmesi için "Otomatik — günlük TCMB kuru" modunu seçin.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s4.title`, '4. Online tahsilat banka hesabını seçin'),
      text: t(`${G}.s4.text`, '"Online Tahsilat Banka Hesabı" kartında seçtiğiniz banka hesabına online sipariş tahsilatları ve carilerin online bakiye yüklemeleri işlenir. Sağlayıcı üzerinden yapılan para iadelerinde banka çıkışı da bu hesaptan yazılır.'),
      tips: [
        t(`${G}.s4.tip1`, 'Banka hesabı seçilmezse online tahsilatların muhasebeleştirilmesi (fatura + cari kayıt) reddedilir ve bayiler online bakiye yükleyemez.'),
      ],
    },
    {
      region: 'footer',
      title: t(`${G}.s5.title`, '5. Kaydedin'),
      text: t(`${G}.s5.text`, 'Döviz, banka hesabı, B2C kaydı ve KDV hariç fiyat ayarları sayfanın altındaki "Kaydet" düğmesine basınca kaydedilir. Sonuç sağ altta kısa bir mesajla bildirilir.'),
    },
    {
      region: 'tabs',
      title: t(`${G}.s6.title`, '6. Cari ekstreyi inceleyin'),
      text: t(`${G}.s6.text`, '"Cari Ekstre" sekmesinde "Cari Hesap" alanından bir bayi / cari seçin, isterseniz Başlangıç ve Bitiş tarihi girin ve "Ekstreyi Getir"e basın. Hareketler en yeni üstte olacak şekilde Tarih, Açıklama, Belge No, Tip, Borç, Alacak ve Bakiye sütunlarıyla listelenir.'),
      tips: [
        t(`${G}.s6.tip1`, 'Cari seçilince üstte Güncel Bakiye, varsa Risk Limiti ve bayi ise "Bayi (B2B)" etiketi görünür.'),
        t(`${G}.s6.tip2`, 'Bu sekme yalnız görüntüleme içindir; kayıt değiştirmez.'),
        t(`${G}.s6.tip3`, 'Sekmelerin sağındaki (i) bu rehberi yeniden açar.'),
      ],
    },
  ],
});
